import * as React from "react";
import {
  ApiResponse,
  PushConnectionError,
  Account,
  AuthenticationResponse,
  SwanPublicClient,
  TradingAccount
} from "swan-public-api-client";
import {
  saveTradingAccounts,
  saveClientAccount,
  saveLinkedTradingAccounts,
  saveActiveTradingAccount
} from "../Platform/State";
import { FxRatesProvider } from "./FxRates";

export interface PapiAuthenticationContextState extends AuthenticationState {
  login: (
    username: string,
    password: string,
    accountOperatorKey: string
  ) => Promise<LoginResult>;
  loginFromToken: () => Promise<void>;
}

const defaultAuthenticationContext: PapiAuthenticationContextState = {
  login: () => {
    return Promise.resolve({ success: false });
  },
  loginFromToken: () => {
    return Promise.resolve();
  },
  client: undefined,
  isAuthenticated: false,
  authenticationInProgress: false,
  // tslint:disable-next-line: no-empty
  deauthenticate: () => {}
};

const PapiAuthenticationContext = React.createContext(
  defaultAuthenticationContext
);

interface ActiveTradingAccountContextState {
  activeTradingAccount?: TradingAccount;
}

const ActiveTradingAccountContext = React.createContext<
  ActiveTradingAccountContextState
>({});

interface AuthenticationState {
  client: SwanPublicClient | undefined;
  isAuthenticated: boolean;
  authenticationInProgress: boolean;
  deauthenticate: () => void;
}

export interface PropsFromState {
  existingToken?: string;
  activeTradingAccount?: TradingAccount;
}

export interface PropsFromActions {
  setTradingAccounts: typeof saveTradingAccounts;
  setLinkedTradingAccounts: typeof saveLinkedTradingAccounts;
  setClientAccount: typeof saveClientAccount;
  setActiveTradingAccount: typeof saveActiveTradingAccount
}

interface AuthenticationProps extends PropsFromState, PropsFromActions {
  url: string;
  children: React.ReactNode
}

export type LoginResult =
  | { success: true; authenticationResponse: AuthenticationResponse }
  | { success: false };

// tslint:disable-next-line: max-classes-per-file
export class PapiAuthenticationProvider extends React.Component<
  AuthenticationProps,
  AuthenticationState
> {
  private client: SwanPublicClient;
  private existingToken?: string;
  private url: string;
  constructor(props: AuthenticationProps) {
    super(props);

    const { url, existingToken } = props;
    this.url = url;
    this.client = new SwanPublicClient(url, existingToken);
    this.existingToken = props.existingToken;
    this.state = {
      isAuthenticated: false,
      client: undefined,
      authenticationInProgress: false,
      // tslint:disable-next-line: no-empty
      deauthenticate: () => {}
    };
  }
  public login = async (
    username: string,
    password: string,
    accountOperatorKey: string
  ): Promise<LoginResult> => {
    let result: LoginResult = { success: false };
    try {
      const response = await this.client.authenticate(
        username,
        password,
        accountOperatorKey,
        // tslint:disable-next-line:no-empty
        x => {}, // console.log(x),
        (x: PushConnectionError, message: string) => {
          console.error(x);
          // todo: what to do when LS errors?
          // this.setState({isAuthenticated: false});
        }
      );
      result = { success: true, authenticationResponse: response.Content };
      const accountResponse = await this.client.getAccount();
      this.updateAuthenticatedState(accountResponse);
    } catch (err) {
      console.error(err);
    }

    return result;
  };
  public loginFromToken = async () => {
    const accountResponse = await this.client.getAccount();
    const config = await this.client.getLightstreamerConfiguration();
    this.client.connectToPush(
      config.Content,
      // tslint:disable-next-line:no-empty
      x => {}, // console.log(x),
      (x: PushConnectionError, message: string) => {
        console.error(x);
        // todo: what to do when LS errors?
        // this.setState({isAuthenticated: false});
      }
    );
    this.updateAuthenticatedState(accountResponse);
  };
  public componentWillMount = async () => {
    if (this.existingToken) {
      try {
        this.setState({ authenticationInProgress: true });
        await this.loginFromToken();
      } catch (ex) {
        console.error("existing session token could not be used to login.", ex);
      }
      this.setState({ authenticationInProgress: false });
    }
  };
  public render() {
    const authenticationContextState: PapiAuthenticationContextState = {
      login: this.login,
      loginFromToken: this.loginFromToken,
      isAuthenticated: this.state.isAuthenticated,
      client: this.state.client,
      authenticationInProgress: this.state.authenticationInProgress,
      deauthenticate: this.deauthenticate
    };
    const activeTradingAccountContextState: ActiveTradingAccountContextState = {
      activeTradingAccount: this.props.activeTradingAccount
    };
    return (
      <PapiAuthenticationContext.Provider value={authenticationContextState}>
        <ActiveTradingAccountContext.Provider
          value={activeTradingAccountContextState}
        >
          {this.props.children}
        </ActiveTradingAccountContext.Provider>
      </PapiAuthenticationContext.Provider>
    );
  }
  private updateAuthenticatedState = (
    accountResponse: ApiResponse<Account>
  ) => {
    this.props.setClientAccount(accountResponse.Content);
    this.props.setTradingAccounts(accountResponse.Content.TradingAccounts);
    this.props.setLinkedTradingAccounts(accountResponse.Content.LinkedTradingAccounts);
    this.setState({
      isAuthenticated: true,
      client: this.client
    });
  };
  private deauthenticate = () => {
    this.client.deauthenticate();
    this.props.setActiveTradingAccount(undefined);

    this.client = new SwanPublicClient(this.url, undefined);
    this.setState({
      client: undefined,
      isAuthenticated: false
    });
  };
}

export const PapiAuthenticationConsumer = PapiAuthenticationContext.Consumer;

interface PapiClientContextState {
  client: SwanPublicClient;
}

// use a cast from an empty object as the required default object so that we can expose `PapiClientContextState` instead of `PapiClientContextState | undefined`
const PapiClientContext = React.createContext<PapiClientContextState>(
  {} as PapiClientContextState
);

// tslint:disable-next-line:max-classes-per-file
export class PapiClientProvider extends React.Component<
  PapiClientContextState
> {
  public render() {
    return (
      <PapiClientContext.Provider value={this.props}>
        <FxRatesProvider swanApiClient={this.props.client}>
          {this.props.children}
        </FxRatesProvider>
      </PapiClientContext.Provider>
    );
  }
}

export const PapiClientConsumer = PapiClientContext.Consumer;
export const ActiveTradingAccountConsumer =
  ActiveTradingAccountContext.Consumer;
